import { Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import TextInput from "./TextInput";
import { feetInchesToCm } from "../../utils";
import { MeasuringSystem } from "../../types";
import { useApi } from "../../contexts/ApiContext";

interface HeightInputProps {
  system: MeasuringSystem;
  value: number | null;
  onChange: (value: number | null) => void;
  error: boolean;
  helperText: string;
  isRequired: boolean;
  key?: string;
  id?: string;
  label: string;
}

const HeightInput: React.FC<HeightInputProps> = ({
  system,
  onChange,
  error,
  helperText,
  key,
  label,
  id,
}) => {
  const [heightCm, setHeightCm] = useState<number | null>(null);
  const [heightFeet, setHeightFeet] = useState<number | null>(null);
  const [heightInches, setHeightInches] = useState<number | null>(null);
  const { isSubmitting, isLoading } = useApi();
  const isDisabled = isLoading || isSubmitting;

  const heightValueCm = useMemo(() => {
    if (system === "imperial") {
      if (heightFeet === null || heightInches === null) {
        return null;
      }
      return Number(
        feetInchesToCm(heightFeet ?? 0, heightInches ?? 0).toFixed(0)
      );
    }

    return heightCm;
  }, [heightCm, heightFeet, heightInches]);

  useEffect(() => {
    onChange(heightValueCm);
  }, [heightValueCm]);

  return (
    <Stack
      id={id}
      width={"100%"}
      key={key}
      direction={"row"}
      alignItems={"flex-end"}
      gap={2}
    >
      {system === "imperial" ? (
        <Stack
          width={"100%"}
          gap={1}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-end"}
        >
          <TextInput
            disabled={isDisabled}
            label={label}
            value={heightFeet?.toString() ?? ""}
            onChange={(value: string | number) =>
              setHeightFeet(parseInt(value as string))
            }
            error={error}
            helperText={" "}
            isRequired={true}
            adornment={"ft"}
            type="integer"
            fullWidth
          />
          <TextInput
            label={""}
            disabled={isDisabled}
            value={heightInches?.toString() ?? ""}
            onChange={(value: string | number) =>
              setHeightInches(value === null ? null : parseInt(value as string))
            }
            error={error}
            helperText={" "}
            isRequired={true}
            adornment={"in"}
            type="integer"
            fullWidth
          />
        </Stack>
      ) : (
        <TextInput
          label={label}
          disabled={isDisabled}
          type="integer"
          value={heightCm === null ? "" : heightCm?.toString() ?? ""}
          onChange={(value: string | number) =>
            setHeightCm(value === null ? null : (value as number))
          }
          error={error}
          helperText={helperText}
          isRequired={true}
          adornment={"cm"}
        />
      )}
    </Stack>
  );
};

export default HeightInput;
